import React, { useState } from 'react';
import { MONTHS_IN_YEAR, revenueProgress } from '../utils/revenueProgress';

// The cumulative Umsatz curve, under the Monatsumsatz bars. Every number comes from
// `utils/revenueProgress.js`; only geometry is derived here.

const MONTHS_SHORT = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];
const MONTH_NAMES  = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];

// Same VB_W / PAD_L / PAD_R as the Monatsumsatz chart, so the month slots line up.
const VB_W = 770, VB_H = 250;
const PAD_L = 65, PAD_R = 10, PAD_T = 22, PAD_B = 34;
const cW = VB_W - PAD_L - PAD_R;
const cH = VB_H - PAD_T - PAD_B;
const slotW = cW / MONTHS_IN_YEAR;

// The viewBox scales strokes and dashes down with the card: a fine dash stops reading as
// dashed at 400px, hence the coarse values.
const SERIES_W = 2.2;
const PREV_DASH = '9 5';
const TARGET_DASH = '7 5';
const PROJECTION_DASH = '3 4';

function fmtEur(n) {
    // Both fraction digits: the minimum alone leaves the maximum at 3 (see CLAUDE.md).
    return `€ ${Number(n || 0).toLocaleString('de-DE', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
}

function fmtK(v) {
    if (!v) return '0';
    if (v >= 10000) return `${(v / 1000).toFixed(0)}k`;
    if (v >= 1000)  return `${(v / 1000).toFixed(1)}k`;
    return Math.round(v).toString();
}

function fmtPct(f) {
    return `${(f * 100).toLocaleString('de-DE', {
        minimumFractionDigits: 1, maximumFractionDigits: 1,
    })} %`;
}

function fmtSignedPct(f) {
    return `${f < 0 ? '−' : '+'}${fmtPct(Math.abs(f))}`;
}

function fmtSignedEur(n) {
    return `${n < 0 ? '−' : '+'}${fmtEur(Math.abs(n))}`;
}

// A "nice" axis maximum, so the ticks read 0 / 150k / 300k rather than 0 / 137k / 274k.
function niceMax(v) {
    if (!(v > 0)) return 1;
    const mag = Math.pow(10, Math.floor(Math.log10(v)));
    for (const step of [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10]) {
        if (v <= step * mag) return step * mag;
    }
    return 10 * mag;
}

const x = m => PAD_L + m * slotW + slotW / 2;

/** An SVG path through the non-null points of a 12-slot series. */
function linePath(series, yOf) {
    const pts = [];
    series.forEach((v, m) => { if (v !== null && v !== undefined) pts.push(`${x(m)},${yOf(v)}`); });
    return pts.length < 2 ? null : `M${pts.join('L')}`;
}

const PACE_LABEL = {
    ahead: 'Über Plan', ontrack: 'Im Plan', behind: 'Unter Plan',
    none: 'Noch nichts fällig',
};

// `tone` colours the value, `hintTone` the hint: a value that IS the delta carries the
// direction, a neutral reference like "Soll bis heute" must not.
function Stat({ label, value, hint, tone, hintTone }) {
    return (
        <div className={`inv-ytd-stat${tone ? ` inv-ytd-stat--${tone}` : ''}`}>
            <span className="inv-ytd-stat-label">{label}</span>
            <span className="inv-ytd-stat-value">{value}</span>
            {hint && (
                <span className={`inv-ytd-stat-hint${hintTone ? ` inv-ytd-stat-hint--${hintTone}` : ''}`}>
                    {hint}
                </span>
            )}
        </div>
    );
}

// *invoices* arrives unfiltered (year-scoping happens here) and *truncated* says it hit the
// API row cap, which is what `prevYearComplete` needs.
export default function RevenueProgressChart({
    invoices = [], year, target = null, truncated = false, hasNonEur = false,
}) {
    const now = new Date();
    const shownYear = year ?? now.getFullYear();
    const isThisYear = shownYear === now.getFullYear();
    const [hoverMonth, setHoverMonth] = useState(null);

    const p = revenueProgress({ invoices, year: shownYear, target, today: now, truncated });
    const showPrev = p.hasPrevYear;

    // The target is part of the scale, or a goal above the curve falls off the page.
    const dataMax = Math.max(
        ...p.cumulative,
        ...(showPrev ? p.cumulativePrev : [0]),
        p.target || 0,
        p.projection || 0,
        1,
    );
    const axisMax = niceMax(dataMax);
    const yOf = v => PAD_T + cH - (Math.min(v, axisMax) / axisMax) * cH;

    const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => ({
        frac: f, label: fmtK(f * axisMax), y: PAD_T + cH - f * cH,
    }));

    const currentPath = linePath(p.series, yOf);
    const prevPath    = showPrev ? linePath(p.cumulativePrev, yOf) : null;

    // Area wash under the actuals, closed to the baseline.
    const areaPath = currentPath
        ? `${currentPath}L${x(p.lastMonth)},${PAD_T + cH}L${x(0)},${PAD_T + cH}Z`
        : null;

    const hasEnd = p.lastMonth >= 0;
    const endX = hasEnd ? x(p.lastMonth) : 0;
    const endY = hasEnd ? yOf(p.ytd) : 0;

    // The run rate carried to December from the last actual point.
    const showProjection = isThisYear && p.projection != null
        && p.lastMonth >= 0 && p.lastMonth < MONTHS_IN_YEAR - 1 && p.ytd > 0;

    const targetY = p.target ? yOf(p.target) : null;

    return (
        <div className="inv-chart-card">
            <div className="inv-chart-header">
                <h3 className="inv-chart-title">
                    Umsatzverlauf kumuliert {shownYear} (Versendet &amp; Bezahlt)
                    {hasNonEur && <span className="inv-chart-currency-hint"> · in EUR</span>}
                </h3>
                <div className="inv-ytd-legend">
                    <span className="inv-ytd-legend-item">
                        <svg className="inv-ytd-key" viewBox="0 0 18 8" aria-hidden="true">
                            <line x1="1" y1="4" x2="17" y2="4" strokeWidth="2.5" strokeLinecap="round"
                                style={{ stroke: 'var(--chart-series-1)' }} />
                        </svg>
                        {shownYear}
                    </span>
                    {showPrev && (
                        <span className="inv-ytd-legend-item">
                            <svg className="inv-ytd-key" viewBox="0 0 18 8" aria-hidden="true">
                                <line x1="1" y1="4" x2="17" y2="4" strokeWidth="2.5" strokeDasharray="5 3"
                                    strokeLinecap="round" style={{ stroke: 'var(--color-text-secondary)' }} />
                            </svg>
                            {shownYear - 1}
                        </span>
                    )}
                    {p.target != null && (
                        <span className="inv-ytd-legend-item">
                            <svg className="inv-ytd-key" viewBox="0 0 18 8" aria-hidden="true">
                                <line x1="1" y1="4" x2="17" y2="4" strokeWidth="2" strokeDasharray="4 3"
                                    strokeLinecap="round" style={{ stroke: 'var(--color-text)' }} opacity="0.65" />
                            </svg>
                            Ziel
                        </span>
                    )}
                </div>
            </div>

            <svg viewBox={`0 0 ${VB_W} ${VB_H}`} style={{ width: '100%', display: 'block' }}>
                {/* Solid: the only dashed marks here are the references. */}
                {yTicks.map(({ frac, label, y }) => (
                    <g key={frac}>
                        <line x1={PAD_L} y1={y} x2={PAD_L + cW} y2={y}
                            style={{ stroke: 'var(--color-border)' }} strokeWidth="1" />
                        <text x={PAD_L - 8} y={y + 4} textAnchor="end" fontSize="10"
                            style={{ fill: 'var(--color-text-muted)' }}>{label}</text>
                    </g>
                ))}
                <line x1={PAD_L} y1={PAD_T + cH} x2={PAD_L + cW} y2={PAD_T + cH}
                    style={{ stroke: 'var(--color-border-strong)' }} strokeWidth="1" />

                {/* A reference, so ink rather than a series or status colour — the meter
                    below carries whether the number is good. */}
                {targetY !== null && (
                    <g>
                        <line x1={PAD_L} y1={targetY} x2={PAD_L + cW} y2={targetY}
                            strokeWidth="1.6" strokeDasharray={TARGET_DASH} opacity="0.65"
                            style={{ stroke: 'var(--color-text)' }} />
                        <text x={PAD_L + cW} y={targetY - 6} textAnchor="end" fontSize="10"
                            fontWeight="600" style={{ fill: 'var(--color-text-secondary)' }}>
                            {`Ziel ${fmtK(p.target)}`}
                        </text>
                    </g>
                )}

                {/* Context, so painted first and in the de-emphasis gray. */}
                {prevPath && (
                    <path d={prevPath} fill="none" strokeWidth={SERIES_W} strokeDasharray={PREV_DASH}
                        strokeLinecap="round" strokeLinejoin="round"
                        style={{ stroke: 'var(--color-text-secondary)' }} />
                )}

                {areaPath && (
                    <path d={areaPath} stroke="none" style={{ fill: 'var(--chart-series-1-soft)' }} />
                )}
                {showProjection && (
                    <line x1={endX} y1={endY} x2={x(MONTHS_IN_YEAR - 1)} y2={yOf(p.projection)}
                        strokeWidth="1.8" strokeDasharray={PROJECTION_DASH} strokeLinecap="round"
                        opacity="0.55" style={{ stroke: 'var(--chart-series-1)' }} />
                )}
                {currentPath && (
                    <path d={currentPath} fill="none" strokeWidth={SERIES_W}
                        strokeLinecap="round" strokeLinejoin="round"
                        style={{ stroke: 'var(--chart-series-1)' }} />
                )}

                {/* The ring is the surface colour so the dot stays legible over the grid. */}
                {hasEnd && p.ytd > 0 && (
                    <g>
                        <circle cx={endX} cy={endY} r="5.4" style={{ fill: 'var(--color-surface)' }} />
                        <circle cx={endX} cy={endY} r="3.6" style={{ fill: 'var(--chart-series-1)' }} />
                        {/* Below the dot while the projection rises to the upper right,
                            otherwise the label sits on the dotted line. */}
                        <text
                            className="inv-ytd-svg-label"
                            x={endX < PAD_L + cW - 60 ? endX + 9 : endX - 9}
                            y={showProjection ? endY + 15 : endY - 9}
                            textAnchor={endX < PAD_L + cW - 60 ? 'start' : 'end'}
                            fontSize="10.5" fontWeight="700"
                            style={{ fill: 'var(--color-text)' }}>
                            {fmtK(p.ytd)}
                        </text>
                    </g>
                )}

                {MONTHS_SHORT.map((name, month) => {
                    const cur = isThisYear && month === now.getMonth();
                    return (
                        <text key={name} x={x(month)} y={PAD_T + cH + 19}
                            textAnchor="middle" fontSize="10"
                            style={{ fill: cur ? 'var(--chart-series-1)' : 'var(--color-text-secondary)',
                                     fontWeight: cur ? '600' : '400' }}>
                            {name}
                        </text>
                    );
                })}

                {/* Painted last so they stay topmost, full height so the reader aims at a month.
                    A native SVG <title> on an ancestor <g> does not fire. */}
                {MONTHS_SHORT.map((name, month) => (
                    <rect
                        key={`hit-${name}`}
                        x={PAD_L + month * slotW} y={PAD_T}
                        width={slotW} height={cH}
                        fill="transparent"
                        data-testid={`ytd-hit-${month}`}
                        onMouseEnter={() => setHoverMonth(month)}
                        onMouseLeave={() => setHoverMonth(null)}
                    />
                ))}

                {hoverMonth !== null && (() => {
                    const cum  = p.series[hoverMonth];
                    const prev = p.cumulativePrev[hoverMonth];
                    const soll = p.target != null
                        ? p.target * ((hoverMonth + 1) / MONTHS_IN_YEAR) : null;
                    const rows = [
                        // Never lead with "Netto ": InvoiceOverview's tests assert no such
                        // text exists while the bar chart above is unhovered.
                        ['Kumuliert', cum === null ? '—' : fmtEur(cum), 'var(--color-text)'],
                        showPrev ? [`${shownYear - 1}`, fmtEur(prev), 'var(--color-text-secondary)'] : null,
                        soll !== null ? ['Ziel anteilig', fmtEur(soll), 'var(--color-text-muted)'] : null,
                    ].filter(Boolean);

                    const TW = 186, TH = 22 + rows.length * 15;
                    const cx = x(hoverMonth);
                    const tx = Math.min(Math.max(cx - TW / 2, PAD_L), PAD_L + cW - TW);
                    const anchorY = cum !== null ? yOf(cum) : PAD_T + cH;
                    const ty = anchorY - TH - 10 >= PAD_T ? anchorY - TH - 10 : PAD_T + 4;
                    return (
                        <g style={{ pointerEvents: 'none' }}>
                            <line x1={cx} y1={PAD_T} x2={cx} y2={PAD_T + cH}
                                strokeWidth="1" style={{ stroke: 'var(--color-border-strong)' }} />
                            {cum !== null && (
                                <circle cx={cx} cy={yOf(cum)} r="3.4"
                                    style={{ fill: 'var(--chart-series-1)' }} />
                            )}
                            <rect x={tx} y={ty} width={TW} height={TH} rx="4"
                                style={{ fill: 'var(--color-surface)',
                                         stroke: 'var(--color-border-strong)' }}
                                strokeWidth="1" />
                            <text x={tx + 10} y={ty + 15} fontSize="10" fontWeight="700"
                                style={{ fill: 'var(--color-text)' }}>
                                {`${MONTH_NAMES[hoverMonth]} ${shownYear}`}
                            </text>
                            {rows.map(([label, value, fill], i) => (
                                <text key={label} x={tx + 10} y={ty + 30 + i * 15} fontSize="10"
                                    style={{ fill }}>
                                    {`${label} ${value}`}
                                </text>
                            ))}
                        </g>
                    );
                })()}
            </svg>

            <div className="inv-ytd-stats">
                {/* "Kumuliert", not "Umsatz {year}": that is the KPI card's label above,
                    and a duplicate makes its getByText ambiguous. */}
                <Stat
                    label="Kumuliert"
                    value={fmtEur(p.ytd)}
                    hint={isThisYear
                        ? `Stand ${MONTHS_SHORT[p.lastMonth] || '—'} ${shownYear}`
                        : `Gesamtjahr ${shownYear}`}
                />
                {showPrev ? (
                    <Stat
                        label={`vs. ${shownYear - 1}`}
                        value={p.prevDeltaPct === null ? '—' : fmtSignedPct(p.prevDeltaPct)}
                        hint={`${fmtSignedEur(p.prevDelta)} · Vorjahr ${fmtEur(p.prevSamePeriod)}`}
                        tone={p.prevDelta < 0 ? 'warn' : 'good'}
                    />
                ) : (
                    <Stat
                        label={`vs. ${shownYear - 1}`}
                        value="—"
                        hint={p.prevYearComplete
                            ? 'Keine Rechnungen im Vorjahr'
                            : 'Vorjahr nicht vollständig geladen'}
                    />
                )}
                {p.target != null && (
                    <>
                        <div className="inv-ytd-stat inv-ytd-stat--meter">
                            <span className="inv-ytd-stat-label">
                                Zielerreichung
                                <span className="inv-card-hint"> · {fmtEur(p.target)}</span>
                            </span>
                            <span className="inv-ytd-stat-value">{fmtPct(p.attainment)}</span>
                            <span className={`inv-ytd-meter inv-ytd-meter--${p.pace || 'none'}`}>
                                <span
                                    className="inv-ytd-meter-fill"
                                    style={{ width: `${Math.min(100, Math.max(0, p.attainment * 100))}%` }}
                                />
                                {/* Where the linear plan stands today, so the bar says
                                    "behind" without needing the number beside it. */}
                                {isThisYear && (
                                    <span className="inv-ytd-meter-pace"
                                        style={{ left: `${Math.min(100, p.elapsed * 100)}%` }} />
                                )}
                            </span>
                            <span className="inv-ytd-stat-hint">{PACE_LABEL[p.pace]}</span>
                        </div>
                        <Stat
                            label="Soll bis heute"
                            value={fmtEur(p.paceTarget)}
                            hint={`Abweichung ${fmtSignedEur(p.paceDelta)}`}
                            // Graded by `pace`, not the raw sign: orange beside an "Im Plan"
                            // label contradicts it.
                            hintTone={p.pace === 'behind' ? 'warn' : p.pace === 'ahead' ? 'good' : undefined}
                        />
                    </>
                )}
                {/* Last, and only for the running year: on a finished one the projection is
                    just the total again, which the Kumuliert card already shows. */}
                {isThisYear && p.projection != null && (
                    <Stat
                        label="Hochrechnung"
                        value={fmtEur(p.projection)}
                        hint="Jahresende bei gleichem Tempo"
                    />
                )}
            </div>

            {p.target == null && (
                <p className="inv-ytd-note">
                    Kein Umsatzziel für {shownYear} hinterlegt — unter Einstellungen · Firma
                    setzbar, dann zeigt der Verlauf zusätzlich Ziellinie, Zielerreichung und
                    die Abweichung vom Soll.
                </p>
            )}
            {!p.prevYearComplete && (
                <p className="inv-ytd-note">
                    Der Vorjahresvergleich ist ausgeblendet: es wurden nicht alle Rechnungen
                    geladen, ein Teiljahr würde wie ein Umsatzeinbruch aussehen.
                </p>
            )}
        </div>
    );
}
